import { useEffect, useState } from 'react';
import {
  Box, Grid,
} from '@mui/material';
import { v4 as uuid } from 'uuid';
import EditableText from '../../Form/EditableText';
import CloseButton from '../../Buttons/Close';
import Utils from '../../../utils/Utils';
import BlockProps from '../../../models/BlockProps';
import BlockTitleTextButtonData from './BlockTitleTextButtonData';
import BlockData from '../../../models/BlockData';
import LinkButton from '../../Buttons/LinkButton';
import BlockComponentButton from '../../../models/BlockComponentValues/BlockComponentButton';
import BlockSettings, { BlockAlign, BlockHeight } from '../../../models/BlockSettings';
import GridWithSettings from '../../Layout/GridWithSettings';
import styles from './BlockTitleTextButton.module.css';
import { useGetBase64Image } from '../../../hooks/UseGetBase64Image';
import BlockComponentImage from '../../../models/BlockComponentValues/BlockComponentImage';
import BackGroundImageModal from '../../Modals/ImageModal/BackGroundImageModal';
import useGetBackground from '../../../hooks/UseGetBackground';
import useParallaxScript from '../../../hooks/useParallaxScript';
import config from '../../../config';

export interface BlockTitleTextButtonProps extends BlockProps {
  blockData: BlockData[]
}

function BlockTitleTextButton({
  data, isEditing, isTemplate, editHandler, blockData, previewMode, showImageModal, onCloseImageModal, mainColor,
}: BlockTitleTextButtonProps) {
  const [props, setProps] = useState<BlockTitleTextButtonData>(data.data as BlockTitleTextButtonData);

  useEffect(() => {
    setProps(data.data as BlockTitleTextButtonData);
  }, [data.data]);

  const [minHeight, setMinHeight] = useState('');
  const [maxHeight, setMaxHeight] = useState('');
  const [gridSettings, setGridSettings] = useState<BlockSettings>(props.settings);

  const handleUpdateSettings = (settings?: BlockSettings) => {
    props.settings = settings;
    setGridSettings(settings);
    editHandler(data.id, props);
  };

  useEffect(() => {
    if (props.height === BlockHeight.TALL) {
      setMinHeight('600px');
      setMaxHeight(null);
    } else {
      setMinHeight(null);
    }

    setGridSettings(props.settings);
  }, [props.height, props.settings]);

  const handleTitleUpdate = (text?: string) => {
    props.title.value = text;
    editHandler(data.id, props);
  };

  const handleDescriptionUpdate = (text?: string) => {
    props.description.value = text;
    editHandler(data.id, props);
  };

  const handleUpdateButton = (button?: BlockComponentButton) => {
    props.button = button;
    editHandler(data.id, props);
  };

  let titleStyle = styles.title;
  if (previewMode === 'smartphone') {
    titleStyle = styles.blockStretched;
  } else if (props.settings.align === BlockAlign.STRETCH) {
    titleStyle = styles.blockStretched;
  } else if (props.settings.align === BlockAlign.CENTER) {
    titleStyle = styles.titleStretched;
  } else {
    titleStyle = styles.title;
  }

  let blockStyle = styles.block;
  if (previewMode === 'smartphone') {
    blockStyle = styles.blockStretched;
  } else if (props.settings.align === BlockAlign.STRETCH) {
    blockStyle = styles.blockStretched;
  } else {
    blockStyle = styles.block;
  }

  const base64Image = useGetBase64Image(props.url?.value);
  const [isEditableTextOpen, setIsEditableTextOpen] = useState(false);

  const elementUuid = uuid();
  useParallaxScript(props.url?.settings?.parallaxBool, elementUuid, config.backgroundImage.parallaxEffect);

  return (
    <Grid
      id={elementUuid}
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
      sx={{
        minHeight: `${minHeight}`,
        maxHeight: `${maxHeight}`,
        overflow: 'hidden',
        ...useGetBackground(
          props.url?.solidColor,
          props.url?.value,
          props.url?.settings?.scale,
          props.url?.settings?.scaleBool,
        ),
        backgroundPosition: 'center 0px',
      }}
    >
      {(isEditing && !isTemplate) && (
        <BackGroundImageModal
          backgroundImage={props.url}
          isShowModal={showImageModal}
          onCloseModal={onCloseImageModal}
          handleOnConfirm={(url: BlockComponentImage) => {
            const tempBg = { ...props, url: { ...url } };
            setProps(tempBg);
            editHandler(data.id, tempBg);
          }}
        />
      )}
      <Box
        // container
        // spacing={0}
        // direction="column"
        // alignItems="center"
        // justifyContent="center"
        className="block-content-wrapper"
      >
        <GridWithSettings
          settings={gridSettings}
          updateHandler={handleUpdateSettings}
          isEditing={isEditing}
          hideJustify
          isEditableTextOpen={isEditableTextOpen}
        >
          {props.title && (
            <Grid className={titleStyle}>
              <Box
                sx={
                  [Utils.hoverBorder(isEditing), {
                    display: 'inline-block',
                    maxWidth: '100%',
                    maxHeight: '100%',
                    height: '100%',
                    width: '100%',
                  }]
                }
              >

                {isEditing && (
                  <CloseButton updateHandler={() => {
                    props.title = undefined;
                    editHandler(data.id, props);
                  }}
                  />
                )}

                <EditableText
                  text={props.title.value}
                  textColor={props.title.settings?.textColor}
                  isEditable={isEditing && !isTemplate}
                  updateHandler={handleTitleUpdate}
                  onChangeModalOpen={(isOpen) => {
                    setIsEditableTextOpen(isOpen);
                  }}
                />
              </Box>
            </Grid>
          )}

          {props.description && (
            <Grid
              item
              xs={12}
              className={blockStyle}
              sx={Utils.hoverBorder(isEditing, {
                marginTop: 2,
              })}
            >

              {isEditing && (
                <CloseButton updateHandler={() => {
                  props.description = undefined;
                  editHandler(data.id, props);
                }}
                />
              )}

              <EditableText
                text={props.description.value}
                textColor={props.description.settings?.textColor}
                isEditable={isEditing && !isTemplate}
                updateHandler={handleDescriptionUpdate}
                onChangeModalOpen={(isOpen) => {
                  setIsEditableTextOpen(isOpen);
                }}
              />

            </Grid>
          )}

          {props.button && (
            <Grid
              item
              className={blockStyle}
              xs={12}
              sx={Utils.hoverBorder(isEditing, {
                marginTop: 2,
              })}
            >

              {isEditing && (
                <CloseButton updateHandler={() => {
                  props.button = undefined;
                  editHandler(data.id, props);
                }}
                />
              )}

              <LinkButton
                mainColor={mainColor}
                button={props.button}
                isEditable={isEditing && !isTemplate}
                isTemplate={isTemplate}
                updateHandler={handleUpdateButton}
                blockData={blockData}
              />

            </Grid>
          )}
        </GridWithSettings>
      </Box>

    </Grid>
  );
}

export default BlockTitleTextButton;
